import EventBusyIcon from "@mui/icons-material/EventBusy";
import PersonOffIcon from "@mui/icons-material/PersonOff";

/**
 * Turn the API's `closureReasons` into EmptyState props.
 *
 * An empty day is not self-explanatory, and the remedy differs by cause. A court
 * shut for maintenance is fixed by dropping the court filter, not by moving to
 * another date — so the generic "try a different date" empty state pushes the
 * client away from a booking the club could still take. Two messages, one per
 * resource, plus the lever the client actually has.
 *
 * That lever is not always there: a private lesson has no court filter at all,
 * and a club can switch resource picking off entirely. With nothing to clear the
 * date IS the only move, so it becomes the action.
 */

const COPY = {
  court: {
    title: "Court unavailable",
    closed: "The court is closed on this date.",
    remedy: "Other courts may still be free at this time.",
    actionLabel: "Show other courts",
    icon: EventBusyIcon
  },
  trainer: {
    title: "Trainer unavailable",
    closed: "The trainer is not available on this date.",
    remedy: "Other trainers may still be free at this time.",
    actionLabel: "Show other trainers",
    icon: PersonOffIcon
  }
};

// Both the pinned resource and the whole-venue case read the same to a client:
// what they picked cannot be had today.
const RESOURCE_BY_KIND = {
  FACILITY_CLOSED: "court",
  ALL_FACILITIES_CLOSED: "court",
  TRAINER_OFF: "trainer",
  ALL_TRAINERS_OFF: "trainer"
};

/**
 * @param {Object} params
 * @param {Array} params.closureReasons - from the availability response
 * @param {boolean} params.canClearResource - is the filter the client's to clear?
 * @param {function} params.onClearResource - drop the resource filter
 * @param {function} params.onNextDate - move to another date
 * @returns {{ title: string, description: string, actionLabel: string, onAction: function }|null}
 */
export const unavailableReasonProps = ({ closureReasons, canClearResource, onClearResource, onNextDate }) => {
  const reason = (closureReasons || [])[0];
  const resource = reason && RESOURCE_BY_KIND[reason.kind];
  if (!resource) return null;

  const copy = COPY[resource];
  const clearable = canClearResource && reason.kind !== "ALL_FACILITIES_CLOSED" && reason.kind !== "ALL_TRAINERS_OFF";

  return {
    icon: copy.icon,
    title: copy.title,
    description: clearable ? `${copy.closed} ${copy.remedy}` : copy.closed,
    actionLabel: clearable ? copy.actionLabel : "Try another date",
    onAction: clearable ? onClearResource : onNextDate
  };
};
